import { useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import {
  FUND_SUMMARY_BY_MD_CAP_IQ_API_ERROR,
  FUND_SUMMARY_BY_MD_DEFAULT_ERROR_MESSAGE,
  FUND_SUMMARY_BY_MD_QUERY_KEY,
  SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/funds';
import { SuperAgentError } from 'common/types/services';
import { FundService } from 'services';
import { throwError } from 'utilities';
import {
  GetFundSummaryByMDParams,
  GetFundSummaryByMDResponse,
  GetFundSummaryByMDReturn,
  UseGetFundSummaryByMDParams,
} from './useGetFundSummaryByMD.types';

const getFundSummaryByMD = async (params: GetFundSummaryByMDParams): Promise<GetFundSummaryByMDResponse | null> => {
  const { fundId, measurementDateId } = params;

  if (isNil(fundId) && isNil(measurementDateId)) {
    return null;
  }

  const fundService = new FundService();

  try {
    const fundSummary = (await fundService.getFundSummaryByMD(
      toString(fundId),
      toString(measurementDateId)
    )) as GetFundSummaryByMDResponse;

    if (!fundSummary) {
      return throwError(FUND_SUMMARY_BY_MD_DEFAULT_ERROR_MESSAGE);
    }

    return fundSummary;
  } catch (error) {
    const { response } = error as SuperAgentError;

    if (response?.body?.cap_iq_api) {
      return throwError(FUND_SUMMARY_BY_MD_CAP_IQ_API_ERROR, error);
    }

    return throwError(FUND_SUMMARY_BY_MD_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFundSummaryByMD = (params: UseGetFundSummaryByMDParams): GetFundSummaryByMDReturn => {
  const { fund, measurementDate, shouldQueryAutomatically = true } = params;

  const fundId = fund?.id;
  const measurementDateId = measurementDate?.id;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<GetFundSummaryByMDReturn['data'], Error>({
    enabled: !isNil(fundId) && !isNil(measurementDateId) && shouldQueryAutomatically,
    queryFn: () => getFundSummaryByMD({ fundId, measurementDateId }),
    queryKey: [FUND_SUMMARY_BY_MD_QUERY_KEY, fundId, measurementDateId],
    refetchOnWindowFocus: SERVICES_FUNDS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export { useGetFundSummaryByMD, getFundSummaryByMD };
